import { useTranslation } from "react-i18next";
import { TWclassNames } from "../../utils/Div";
import { lineAmount } from "./utils/offer-workspace";

/**
 * Offer feature-flag helpers (company flags from list envelope / offer as_json).
 * Canonical keys: offer_v2_overview, offer_revisions, offer_calculation_v2, offer_inquiries, …
 */
export function isOfferFeatureEnabled(flags, key) {
  if (!flags || !key) return false;
  return Boolean(flags[key]);
}

const EDITABLE_DRAFT_STATUSES = new Set(["draft", "pending_review"]);

/**
 * True when kalkyle must be read-only: offer_revisions on and no editable draft
 * (typical after internal approve clears current_draft_revision_id).
 * Prefers authoritative `offer.calculation_locked` from API when present.
 */
export function isOfferCalculationLocked(offer, flags) {
  if (!offer) return false;
  if (typeof offer.calculation_locked === "boolean") {
    return offer.calculation_locked;
  }

  const featureFlags = flags || offer.offer_feature_flags || {};
  if (!isOfferFeatureEnabled(featureFlags, "offer_revisions")) return false;

  const draft = offer.current_draft_revision;
  const draftId = offer.current_draft_revision_id;
  if (draftId) {
    if (!draft || EDITABLE_DRAFT_STATUSES.has(String(draft.status || ""))) {
      return false;
    }
  }

  // Fallback without calculation_locked: treat missing draft pointer + known
  // locked history as locked (accepted / revision chip / revision number).
  if (draftId) return false;
  return Boolean(
    offer.accepted_revision_id ||
      offer.accepted_revision ||
      offer.accepted_revision_number != null ||
      (offer.revision_number != null && offer.revision_number > 0)
  );
}

/**
 * Header overflow «Opprett ny revisjon» — revisions on and reopen/edit makes sense
 * (kalkyle locked / no editable draft after locked history).
 */
export function shouldShowCreateRevisionMenuItem(offer, featureFlags) {
  const flags = featureFlags || offer?.offer_feature_flags || {};
  if (!isOfferFeatureEnabled(flags, "offer_revisions")) return false;
  if (!offer?.id) return false;
  if (isOfferCalculationLocked(offer, flags)) return true;

  const draft = offer?.current_draft_revision;
  const draftId = offer?.current_draft_revision_id;
  if (draftId) {
    if (!draft || EDITABLE_DRAFT_STATUSES.has(String(draft.status || ""))) {
      return false;
    }
  }

  return Boolean(
    offer?.accepted_revision_id ||
      offer?.accepted_revision ||
      offer?.accepted_revision_number != null ||
      (offer?.revision_number != null && Number(offer.revision_number) > 0)
  );
}

const NOT_AUTHORIZED_TOKENS = new Set([
  "not_authorized",
  "not authorized",
  "access_denied",
  "access denied",
]);

function normalizeAuthErrorToken(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim().toLowerCase();
  if (typeof value?.message === "string") return value.message.trim().toLowerCase();
  return "";
}

/**
 * Detect Offer API 403 / not_authorized from soft error payloads or axios rejects.
 * Sensitive actions (send/accept/convert/import/revisions) return this after CanCan hardening.
 */
export function isOfferNotAuthorized(payload) {
  if (!payload) return false;

  const status =
    payload.status ??
    payload.statusCode ??
    payload.response?.status ??
    payload.response?.statusCode;
  if (status === 403) return true;

  const candidates = [
    payload.error,
    payload.message,
    payload.response?.data?.error,
    payload.response?.data?.message,
  ];

  const errors = payload.errors ?? payload.response?.data?.errors;
  if (Array.isArray(errors)) {
    candidates.push(...errors);
  } else if (errors != null) {
    candidates.push(errors);
  }

  return candidates.some((item) => NOT_AUTHORIZED_TOKENS.has(normalizeAuthErrorToken(item)));
}

/**
 * User-facing message for offer action failures; prefers i18n for not_authorized.
 */
export function resolveOfferActionErrorMessage(t, payload, fallbackKey) {
  if (isOfferNotAuthorized(payload)) {
    return t("offers.errors.not_authorized");
  }

  const errors = payload?.errors ?? payload?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length) {
    const first = errors[0];
    if (typeof first === "string") {
      if (first === "offer_checklist_required_on_send") {
        return t("offers.send.checklist_required");
      }
      if (first === "rejection_reason_required") {
        return t("offers.approval.reject_reason_required");
      }
      if (first === "offer_revision_locked") {
        return t("offers.errors.revision_locked");
      }
      if (
        first === "offer_calculation_changed_by_another_user" ||
        first === "offer_calculation_stale" ||
        first === "stale_object" ||
        first === "stale_calculation" ||
        first === "calculation_conflict"
      ) {
        return t("offers.tabs.calculation_conflict");
      }
      return first;
    }
    if (first?.message) return first.message;
  } else if (typeof errors === "string" && errors.trim()) {
    if (errors === "offer_checklist_required_on_send") {
      return t("offers.send.checklist_required");
    }
    if (errors === "rejection_reason_required") {
      return t("offers.approval.reject_reason_required");
    }
    if (errors === "offer_revision_locked") {
      return t("offers.errors.revision_locked");
    }
    if (
      errors === "offer_calculation_changed_by_another_user" ||
      errors === "offer_calculation_stale" ||
      errors === "stale_object" ||
      errors === "stale_calculation" ||
      errors === "calculation_conflict"
    ) {
      return t("offers.tabs.calculation_conflict");
    }
    return errors;
  }

  const status = payload?.status ?? payload?.response?.status;
  if (status === 409) {
    return t("offers.tabs.calculation_conflict");
  }

  const err = payload?.error ?? payload?.response?.data?.error;
  if (typeof err === "string" && err.trim() && !NOT_AUTHORIZED_TOKENS.has(normalizeAuthErrorToken(err))) {
    if (
      err === "offer_calculation_changed_by_another_user" ||
      err === "offer_calculation_stale" ||
      err === "stale_object" ||
      err === "stale_calculation" ||
      err === "calculation_conflict"
    ) {
      return t("offers.tabs.calculation_conflict");
    }
    return err;
  }

  return t(fallbackKey);
}

/**
 * Resolve offer_feature_flags for calculation UI.
 * Prefer flags on the calc/offer payload when present; else Redux offerFeatureFlags.
 * Does not mutate or redefine the offerFeatureFlags shape.
 */
export function resolveOfferCalculationFeatureFlags({
  calculation,
  offer,
  offerFeatureFlags,
} = {}) {
  const fromCalc = calculation?.offer_feature_flags;
  if (fromCalc && typeof fromCalc === "object") return fromCalc;
  const fromOffer = offer?.offer_feature_flags;
  if (fromOffer && typeof fromOffer === "object") return fromOffer;
  if (offerFeatureFlags && typeof offerFeatureFlags === "object") {
    return offerFeatureFlags;
  }
  return {};
}

export function isOfferCalculationV2Enabled(flags) {
  return isOfferFeatureEnabled(flags, "offer_calculation_v2");
}

/**
 * Safe formula path (ADR-003): no Function()/eval on the FE hot path.
 * On when offer_safe_formula_engine is set, or when calculation_v2 implies the new stack.
 */
export function isOfferSafeFormulaEngineEnabled(flags) {
  return (
    isOfferFeatureEnabled(flags, "offer_safe_formula_engine") ||
    isOfferFeatureEnabled(flags, "offer_calculation_v2")
  );
}

export function isOfferModuleBuilderEnabled(flags) {
  return isOfferFeatureEnabled(flags, "offer_module_builder");
}

/** Line vs module-builder calculation. Only `module_enabled` counts —
 * API as_json always sends `module_graph`/`module_result` as `{}`, which is
 * truthy in JS and must not hide the line workspace. */
export function isModuleCalculation(calculation) {
  const raw = calculation?.module_enabled;
  return raw === true || raw === "true" || raw === 1 || raw === "1";
}

/** Calc template version history («Malversjoner») — GET/POST /offer_calculations/:id/versions|restore_version. */
export function isOfferCalculationTemplateVersionsEnabled(flags) {
  return isOfferFeatureEnabled(flags, "offer_calculation_template_versions");
}

/** Offer template version history («Versjonshistorikk (mal)») — GET/POST /offers/:id/template_versions|restore_template_version. */
export function isOfferTemplateVersionsEnabled(flags) {
  return isOfferFeatureEnabled(flags, "offer_template_versions");
}

/**
 * Server EvaluateModuleGraph gate (mirrors API FeatureFlags check).
 * offer_module_builder AND (safe formula OR calculation v2).
 */
export function isServerModuleEvaluationEnabled(flags) {
  return (
    isOfferModuleBuilderEnabled(flags) && isOfferSafeFormulaEngineEnabled(flags)
  );
}

/**
 * Prefer API module_result for displayed totals after save/load.
 * Requires server_evaluated on the payload. When offer flags are present, also
 * requires offer_module_builder + (safe formula | calculation v2) so a stale
 * flag does not win after rollout is turned off.
 */
export function shouldPreferServerModuleResult(moduleResult, flags) {
  if (!moduleResult || typeof moduleResult !== "object") return false;
  if (moduleResult.server_evaluated !== true) return false;
  if (flags && typeof flags === "object" && Object.keys(flags).length > 0) {
    return isServerModuleEvaluationEnabled(flags);
  }
  return true;
}

/**
 * Normalize module_result decimal strings (API BigDecimal JSON) for UI display.
 * Returns the same shape as client calculateGraph (numeric total/values/breakdown).
 */
export function normalizeModuleResultForDisplay(moduleResult) {
  if (!moduleResult || typeof moduleResult !== "object") return null;

  const values = {};
  const rawValues = moduleResult.values;
  if (rawValues && typeof rawValues === "object") {
    Object.entries(rawValues).forEach(([key, raw]) => {
      values[key] = parseMoneyAmount(raw) ?? 0;
    });
  }

  const breakdown = Array.isArray(moduleResult.breakdown)
    ? moduleResult.breakdown.map((row) => ({
        ...row,
        value: parseMoneyAmount(row?.value) ?? 0,
      }))
    : [];

  return {
    ...moduleResult,
    total: parseMoneyAmount(moduleResult.total) ?? 0,
    values,
    breakdown,
    warnings: Array.isArray(moduleResult.warnings) ? moduleResult.warnings : [],
    errors: Array.isArray(moduleResult.errors) ? moduleResult.errors : [],
    evaluation_order: Array.isArray(moduleResult.evaluation_order)
      ? moduleResult.evaluation_order
      : [],
    safe_mode: Boolean(moduleResult.safe_mode),
    server_evaluated: Boolean(moduleResult.server_evaluated),
    calculated_at: moduleResult.calculated_at || null,
  };
}

/** Parse API money/decimal fields for display only (no arithmetic). */
export function parseMoneyAmount(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  // Line editors store nb-formatted values after blur ("123,45") — Number()
  // and bare parseFloat stop/NaN on comma decimals.
  const n = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function getRootCalculations(offer) {
  if (!offer || !Array.isArray(offer.calculations)) return [];
  return offer.calculations;
}

/** Sum a numeric field across root calculations; null if none present. */
function sumCalculationAmounts(calculations, pickAmount) {
  let sum = 0;
  let found = false;
  for (const calc of calculations) {
    const n = pickAmount(calc);
    if (n != null) {
      sum += n;
      found = true;
    }
  }
  return found ? sum : null;
}

function pickCalculationSalesAmount(calculation) {
  return (
    parseMoneyAmount(calculation?.total_amount) ??
    parseMoneyAmount(calculation?.subtotal_amount)
  );
}

/**
 * Walk calculation/offer line items and estimate cost from cost_total or
 * purchase_price×qty (via estimateLineCostFromKostpris).
 */
function estimateOfferCostFromItems(offer) {
  if (!offer) return null;
  let sum = 0;
  let found = false;

  const walkItems = (items) => {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      if (!item) continue;
      // Prefer positive cost_total; fall back to kostpris×qty when cache is 0/stale.
      const cached = parseMoneyAmount(item.cost_total);
      if (cached != null && cached > 0) {
        sum += cached;
        found = true;
      } else {
        const estimate = estimateLineCostFromKostpris(item);
        if (estimate != null) {
          sum += estimate;
          found = true;
        } else if (cached != null) {
          sum += cached;
          found = true;
        }
      }
      // Nested children are structure groups — do not recurse (avoids double-count).
    }
  };

  for (const calc of getRootCalculations(offer)) {
    walkItems(calc?.items);
  }
  walkItems(offer.items);
  walkItems(offer.offer_items);

  return found ? sum : null;
}

/**
 * Prefer persisted/backend amounts when present; fall back to sum of root
 * calculation totals, then legacy total_amount.
 */
export function resolveOfferSalesAmount(offer) {
  if (!offer) return 0;
  const cached = offer.subtotal_amount ?? offer.total_amount;
  const n = parseMoneyAmount(cached);
  if (n != null) return n;
  const fromCalcs = sumCalculationAmounts(
    getRootCalculations(offer),
    pickCalculationSalesAmount
  );
  return fromCalcs != null ? fromCalcs : 0;
}

/**
 * Offer cost with fallbacks: offer.cost_total → sum calculation.cost_total →
 * estimate from line items. Returns { amount, authoritative }.
 * Authoritative = offer-level cost_total cache was present.
 */
export function resolveOfferCostMetrics(offer) {
  if (!offer) return { amount: null, authoritative: false };

  const cached = parseMoneyAmount(offer.cost_total);
  if (cached != null) {
    return { amount: cached, authoritative: true };
  }

  const fromCalcs = sumCalculationAmounts(getRootCalculations(offer), (calc) =>
    parseMoneyAmount(calc?.cost_total)
  );
  if (fromCalcs != null) {
    return { amount: fromCalcs, authoritative: false };
  }

  const fromItems = estimateOfferCostFromItems(offer);
  return { amount: fromItems, authoritative: false };
}

/** Prefer offer.cost_total; else calculation / line fallbacks. Null if unavailable. */
export function resolveOfferCostAmount(offer) {
  return resolveOfferCostMetrics(offer).amount;
}

/**
 * Contribution margin (DB): offer cache → sum calc.contribution_margin →
 * derive from calc sales−cost → estimate from resolved offer sales−cost.
 */
export function resolveOfferContributionMargin(offer) {
  if (!offer) return { amount: null, authoritative: false };

  const cached = parseMoneyAmount(offer.contribution_margin);
  if (cached != null) {
    return { amount: cached, authoritative: true };
  }

  const calcs = getRootCalculations(offer);
  const fromCalcs = sumCalculationAmounts(calcs, (calc) =>
    parseMoneyAmount(calc?.contribution_margin)
  );
  if (fromCalcs != null) {
    return { amount: fromCalcs, authoritative: false };
  }

  const calcSales = sumCalculationAmounts(calcs, pickCalculationSalesAmount);
  const calcCost = sumCalculationAmounts(calcs, (calc) =>
    parseMoneyAmount(calc?.cost_total)
  );
  if (calcSales != null && calcCost != null) {
    return { amount: calcSales - calcCost, authoritative: false };
  }

  const cost = resolveOfferCostAmount(offer);
  if (cost == null) return { amount: null, authoritative: false };

  const sales = resolveOfferSalesAmount(offer);
  return { amount: sales - cost, authoritative: false };
}

/**
 * Contribution margin % (DG%): offer cache → single-calc percent →
 * derive from resolved DB / sales.
 */
export function resolveOfferContributionMarginPercent(offer) {
  if (!offer) return { amount: null, authoritative: false };

  const cached = parseMoneyAmount(offer.contribution_margin_percent);
  if (cached != null) {
    return { amount: cached, authoritative: true };
  }

  const calcs = getRootCalculations(offer);
  if (calcs.length === 1) {
    const single = parseMoneyAmount(calcs[0]?.contribution_margin_percent);
    if (single != null) {
      return { amount: single, authoritative: false };
    }
  }

  const margin = resolveOfferContributionMargin(offer);
  if (margin.amount == null) return { amount: null, authoritative: false };

  const sales = resolveOfferSalesAmount(offer);
  if (!(sales > 0)) return { amount: null, authoritative: false };

  return {
    amount: (margin.amount / sales) * 100,
    authoritative: false,
  };
}

/**
 * Authoritative calculation sales total when v2 is on.
 * module_result.total is only used on the legacy path (flag off).
 */
export function resolveCalculationSalesTotal(calculation, { v2 = false } = {}) {
  if (!calculation) return null;
  if (v2) {
    return parseMoneyAmount(calculation.total_amount);
  }
  const moduleTotal = parseMoneyAmount(calculation?.module_result?.total);
  if (moduleTotal != null) return moduleTotal;
  return parseMoneyAmount(calculation.total_amount);
}

export function resolveCalculationCostTotal(calculation, { v2 = false } = {}) {
  if (!calculation || !v2) return parseMoneyAmount(calculation?.cost_total);
  return parseMoneyAmount(calculation.cost_total);
}

export function resolveCalculationContributionMargin(calculation) {
  if (!calculation) return null;
  return parseMoneyAmount(calculation.contribution_margin);
}

export function resolveCalculationContributionMarginPercent(calculation) {
  if (!calculation) return null;
  return parseMoneyAmount(calculation.contribution_margin_percent);
}

/**
 * Line sales display: prefer API line_total when v2; else caller legacy amount.
 * Nested package lines: prefer nested.total_amount × qty when the shell
 * line_total cache is stale (Edit calc updates nested before parent PUT).
 * Returns { amount, authoritative, estimate }.
 */
export function resolveLineSalesDisplay(item, { v2 = false, legacyAmount = null } = {}) {
  const nestedId = item?.nested_calculation_id ?? item?.nested_calculation?.id;
  const nestedUnit = parseMoneyAmount(item?.nested_calculation?.total_amount);
  if (nestedId != null && nestedUnit != null) {
    const qty = parseMoneyAmount(item?.quantity);
    const effectiveQty = qty != null && qty > 0 ? qty : 1;
    const fromNested = lineAmount({
      ...item,
      quantity: effectiveQty,
      sales_price: nestedUnit,
    });
    if (v2) {
      const cached = parseMoneyAmount(item?.line_total);
      // Trust nested when cache missing or clearly behind the module total.
      if (cached == null || Math.abs(cached - fromNested) > 0.009) {
        return { amount: fromNested, authoritative: false, estimate: true };
      }
      return { amount: cached, authoritative: true, estimate: false };
    }
    const legacy = parseMoneyAmount(legacyAmount);
    return {
      amount: legacy != null ? legacy : fromNested,
      authoritative: false,
      estimate: false,
    };
  }

  if (v2) {
    const cached = parseMoneyAmount(item?.line_total);
    let legacy = parseMoneyAmount(legacyAmount);
    const qty = parseMoneyAmount(item?.quantity);
    const price = parseMoneyAmount(item?.sales_price);
    const crude =
      qty != null && qty > 0 && price != null ? qty * price : price || 0;
    const discounted = lineAmount(item);
    // qty×price callers must not beat a discounted live/cache amount.
    if (
      legacy != null &&
      Math.abs(legacy - crude) < 0.009 &&
      Math.abs(discounted - crude) > 0.009
    ) {
      legacy = discounted;
    }
    // Local edits update sales_price/qty before line_total is refreshed (or
    // before autosave merges API caches). Prefer the live FE estimate when
    // it clearly diverges — same pattern as nested packages above.
    if (
      cached != null &&
      legacy != null &&
      Math.abs(cached - legacy) > 0.009
    ) {
      return { amount: legacy, authoritative: false, estimate: true };
    }
    if (cached != null) {
      return { amount: cached, authoritative: true, estimate: false };
    }
    return {
      amount: legacy != null ? legacy : 0,
      authoritative: false,
      estimate: true,
    };
  }
  const legacy = parseMoneyAmount(legacyAmount);
  return {
    amount: legacy != null ? legacy : 0,
    authoritative: false,
    estimate: false,
  };
}

/**
 * Line-editor number fields (purchase_price/hours/cost_hour_rate/quantity) may
 * hold nb-formatted comma-decimal strings between blur events (see
 * calculation-items.js formatNumber/parseFormattedNumber). Parse both comma
 * and dot decimals so the kostpris fallback below doesn't truncate at ",".
 */
function parseLocaleNumber(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const n = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/**
 * offer-bug-p0: local kostpris fallback when the persisted cost_total cache is
 * 0/stale (e.g. right after adding purchase_price or qty before the next save
 * round-trip). API `Offers::CalculationLineMath#cost_amount` only sums
 * qty * purchase_price when BOTH are present, so a qty=0 line legitimately
 * persists cost_total=0 even though a kostpris has been entered locally.
 * Never invents a cost when no kostpris/cost field exists — returns null then.
 */
function estimateLineCostFromKostpris(item) {
  if (!item) return null;
  const rawQty = parseLocaleNumber(item.quantity);
  const effectiveQty = rawQty != null && rawQty > 0 ? rawQty : 1;

  if (item.line_type === "hours") {
    const hours = parseLocaleNumber(item.hours) || 0;
    const costHourRate = parseLocaleNumber(item.cost_hour_rate);
    if (hours > 0 && costHourRate != null && costHourRate > 0) {
      return hours * costHourRate * effectiveQty;
    }
    const purchasePrice = parseLocaleNumber(item.purchase_price);
    if (purchasePrice != null && purchasePrice > 0) {
      return purchasePrice * effectiveQty;
    }
    return null;
  }

  const purchasePrice = parseLocaleNumber(item.purchase_price);
  if (purchasePrice != null && purchasePrice > 0) {
    return purchasePrice * effectiveQty;
  }
  return null;
}

/**
 * Line cost display: prefer authoritative cost_total when v2 and > 0 (backend
 * stays authoritative for persisted totals). Falls back to a kostpris-based
 * UI estimate when the cache is missing/0/stale so sticky material/labor/
 * module totals don't silently drop to empty. See estimateLineCostFromKostpris.
 */
export function resolveLineCostDisplay(item, { v2 = false } = {}) {
  if (!v2) return null;
  const cached = parseMoneyAmount(item?.cost_total);
  const estimate = estimateLineCostFromKostpris(item);
  if (
    cached != null &&
    cached > 0 &&
    estimate != null &&
    Math.abs(cached - estimate) > 0.009
  ) {
    return estimate;
  }
  if (cached != null && cached > 0) return cached;
  return estimate != null ? estimate : cached;
}

/**
 * Offer statuses aligned with OffersController#index filter whitelist.
 * Values are lowercase; Offer#set_status may persist "NEW" — normalize for display.
 * Backend filter for `new` currently matches only status: 'new' (not 'NEW') — see handoff.
 */
export const OFFER_STATUS_VALUES = [
  "none",
  "new",
  "sent",
  "accepted",
  "rejected",
  "expired",
  "cancelled",
];

export const offerStatusMeta = {
  none: {
    labelKey: "offers.status.none",
    class: "bg-slate-100 text-slate-700",
  },
  new: {
    labelKey: "offers.status.new",
    class: "bg-amber-100 text-amber-900",
  },
  sent: {
    labelKey: "offers.status.sent",
    class: "bg-blue-100 text-blue-800",
  },
  accepted: {
    labelKey: "offers.status.accepted",
    class: "bg-green-100 text-green-800",
  },
  rejected: {
    labelKey: "offers.status.rejected",
    class: "bg-red-100 text-red-800",
  },
  expired: {
    labelKey: "offers.status.expired",
    class: "bg-orange-100 text-orange-900",
  },
  cancelled: {
    labelKey: "offers.status.cancelled",
    class: "bg-slate-200 text-slate-800",
  },
};

/** Normalize API/status values for filter + display (NEW → new). */
export function normalizeOfferStatus(status) {
  if (status == null || status === "") return "none";
  return String(status).toLowerCase();
}

export const offerStatusOptions = OFFER_STATUS_VALUES.map((value) => ({
  id: value,
  value,
  title: offerStatusMeta[value].labelKey,
  label: offerStatusMeta[value].labelKey,
  class: offerStatusMeta[value].class,
}));

export const OfferStatus = ({ offer, status: statusProp }) => {
  const { t } = useTranslation();
  const raw = statusProp !== undefined ? statusProp : offer?.status;
  const key = normalizeOfferStatus(raw);
  const meta = offerStatusMeta[key] || offerStatusMeta.none;

  return (
    <span
      className={TWclassNames(
        meta.class,
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold leading-5"
      )}
    >
      {t(meta.labelKey)}
    </span>
  );
};
